import { Button, Card, DatePicker, Popover, Row, Col, Switch, Menu } from 'antd'
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { useTranslation } from '~/common/hooks/useTranslation'
import { itemsGameDashboard, levelKeys } from '~/common/constants'
import { getRandomColor } from '~/helpers/item-chart'
import { useDashboard } from '~/views/app/redux/hooks/useDashboard'
import moment from 'moment'
const { RangePicker } = DatePicker

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
)

const getCountryLabel = (code) => {
  const upper = String(code).toUpperCase()
  const regionCode = upper === 'JA' ? 'JP' : upper
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' })
    const name = dn.of(regionCode)
    if (name && name !== regionCode) {
      return `${name} (${regionCode})`
    }
  } catch (e) {}
  return regionCode
}

const GameChart = ({ handleChangeParam }) => {
  const { t } = useTranslation()
  const { actions } = useDashboard()

  const [checkedList, setCheckedList] = useState([])
  const [date, setDate] = useState(['', ''])
  const [dataChart, setDataChart] = useState([])
  const labels = useMemo(() => dataChart?.map((item) => {
    const formattedDate = item._id
    return formattedDate
  }), [dataChart])

  const [activeMenuChild, setActiveMenuChild] = useState([])

  const [stateOpenKeys, setStateOpenKeys] = useState([])

  const availableCountries = useMemo(() => {
    if (!dataChart || !Array.isArray(dataChart)) return []
    const countrySet = new Set()
    dataChart.forEach((dayData) => {
      const loc = dayData?.firstData?.user?.location
      if (loc && typeof loc === 'object' && !Array.isArray(loc)) {
        Object.keys(loc).forEach((key) => {
          if (key !== 'total' && key !== '_id' && typeof loc[key] === 'number' && loc[key] > 0) {
            const c = key.toLowerCase() === 'ja' ? 'jp' : key.toLowerCase()
            countrySet.add(c)
          }
        })
      } else if (Array.isArray(loc)) {
        loc.forEach((c) => {
          if (c && typeof c === 'string') {
            const code = c.toLowerCase() === 'ja' ? 'jp' : c.toLowerCase()
            countrySet.add(code)
          }
        })
      }
    })
    return Array.from(countrySet).sort()
  }, [dataChart])

  const availableTimezones = useMemo(() => {
    if (!dataChart || !Array.isArray(dataChart)) return []
    const tzSet = new Set()
    dataChart.forEach((dayData) => {
      const tz = dayData?.firstData?.user?.time_zone_distribution
      if (tz && typeof tz === 'object') {
        Object.keys(tz).forEach((key) => {
          if (key !== 'total' && key !== '_id' && typeof tz[key] === 'number' && tz[key] > 0) {
            tzSet.add(key)
          }
        })
      }
    })
    return Array.from(tzSet).sort((a, b) => {
      const parseHours = (str) => {
        const match = str.match(/UTC([+-]?\d+)/i)
        return match ? parseInt(match[1], 10) : 0
      }
      return parseHours(a) - parseHours(b)
    })
  }, [dataChart])

  const dynamicItemsGameDashboard = useMemo(() => {
    return itemsGameDashboard.map((rootItem) => {
      if (rootItem.key === 'user' && rootItem.children) {
        const newChildren = rootItem.children.map((child) => {
          if (child.key === 'location') {
            const locationChildren = [
              {
                value: 'total',
                key: 'location_total',
                label: 'Total',
                chartLabel: 'Location - Total',
              },
              ...availableCountries.map((c) => ({
                value: c,
                key: `location_${c}`,
                label: getCountryLabel(c),
                chartLabel: `Location - ${getCountryLabel(c)}`,
              })),
            ]
            return {
              ...child,
              children: locationChildren,
            }
          }
          if (child.key === 'time_zone_distribution') {
            const timezoneChildren = [
              {
                value: 'total',
                key: 'time_zone_distribution_total',
                label: 'Total',
                chartLabel: 'Timezone - Total',
              },
              ...availableTimezones.map((tz) => ({
                value: tz,
                key: `time_zone_distribution_${tz.replace(/[^a-zA-Z0-9]/g, '_')}`,
                rawTz: tz,
                label: tz,
                chartLabel: `Timezone - ${tz}`,
              })),
            ]
            return {
              ...child,
              children: timezoneChildren,
            }
          }
          return child
        })
        return {
          ...rootItem,
          children: newChildren,
        }
      }
      return rootItem
    })
  }, [availableCountries, availableTimezones])

  const dynamicLevelKeys = useMemo(() => {
    const key = {}
    const func = (items, level = 1) => {
      items?.forEach((item) => {
        if (item.key) {
          key[item.key] = level
        }
        if (item.children) {
          func(item.children, level + 1)
        }
      })
    }
    func(dynamicItemsGameDashboard)
    return key
  }, [dynamicItemsGameDashboard])
  const verticalLinePlugin = {
    id: 'verticalLineOnHover',
    afterDraw: (chart) => {
      if (chart.tooltip._active && chart.tooltip._active.length) {
        const ctx = chart.ctx
        const x = chart.tooltip._active[0].element.x
        const yAxisTop = chart.scales.y.top
        const yAxisBottom = chart.scales.y.bottom

        ctx.save()
        ctx.beginPath()
        ctx.moveTo(x, yAxisTop)
        ctx.lineTo(x, yAxisBottom)
        ctx.lineWidth = 1
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)'
        ctx.setLineDash([5, 5])
        ctx.stroke()
        ctx.restore()
      }
    },
  }

  ChartJS.register(verticalLinePlugin)
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        display: true,
        labels: {
          color: '#fff',
          usePointStyle: true,
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const rawVal = context.raw
            if (typeof rawVal === 'number') {
              if (rawVal === 0) return `${context.dataset.label || ''}: 0`
              const formatted = Number.isInteger(rawVal)
                ? rawVal.toString()
                : rawVal.toFixed(7).replace(/\.?0+$/, '')
              return `${context.dataset.label || ''}: ${formatted}`
            }
            return `${context.dataset.label || ''}: ${context.formattedValue || '0'}`
          },
        },
      },
      borderDash: {
        xAxis: [0, 888],
      },
    },
    verticalLineOnHover: true,
    scales: {
      min: 0,
      y: {
        border: {
          color: '#fff',
        }, // for the grid lines
        grid: {
          display: true,
          color: 'rgba(255, 255, 255, 0.1)',
          tickBorderDash: [1, 10],
          tickColor: 'transparent',
          tickWidth: 2,
          offset: true,
          drawTicks: true,
          drawOnChartArea: true,
        },
        ticks: {
          color: '#fff',
          font: {
            size: 10,
          },
          beginAtZero: true,
          callback: (value) => {
            if (typeof value === 'number') {
              if (value === 0) return '0'
              if (Math.abs(value) < 0.01) {
                return value.toFixed(7).replace(/\.?0+$/, '')
              }
              return Number.isInteger(value) ? value.toString() : value.toFixed(2)
            }
            return value
          },
        },
      },
      x: {
        border: {
          color: '#fff',
        },
        grid: {
          display: true,
          borderDash: [5, 5],
          color: 'rgba(255, 255, 255, 0)',
        },
        ticks: {
          color: '#fff',
          font: {
            size: 10,
          },
          stepSize: 1,
          beginAtZero: true,
        },
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  }
  const getNestedValue = (obj, keys) => {
    return keys.reduce((acc, key) => acc && acc[key], obj)
  }

  const transformKeys = (keys) => {
    return keys.map((key) => {
      if (key === 'farmNFT') return 'farm'
      if (key === 'farm_trait') return 'farm_trait_id'
      return key
    })
  }

  const resolveMetricValue = useCallback((firstData, openKeys, dataItem) => {
    if (!firstData) return 0

    if (dataItem?.key) {
      const keyStr = dataItem.key

      // User metrics (Items 1 - 10)
      if (
        keyStr === 'new_user_today' ||
        keyStr === 'new_user_last_1_week' ||
        keyStr === 'new_user_last_1_month' ||
        keyStr === 'total_user' ||
        keyStr === 'active_user' ||
        keyStr === 'average_play_time' ||
        keyStr === 'average_play_count' ||
        keyStr === 'retention_rate' ||
        keyStr === 'twitter_integration_count' ||
        keyStr === 'number_of_sms_verification'
      ) {
        const val = firstData.user?.[keyStr]
        if (typeof val === 'number') return val
        if (val !== undefined && val !== null) return Number(val) || 0
      }
      if (keyStr === 'location' || keyStr === 'location_total') {
        const val = firstData.user?.location
        if (typeof val === 'number') return val
        if (typeof val === 'object' && val !== null) {
          if (typeof val.total === 'number') return val.total
          if (Array.isArray(val)) return val.length
          return Object.values(val).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
        return 0
      }
      if (keyStr.startsWith('location_')) {
        const country = keyStr.replace('location_', '').toLowerCase()
        const altCountry = country === 'jp' ? 'ja' : country === 'ja' ? 'jp' : null
        const val = firstData.user?.location
        if (val && typeof val === 'object' && !Array.isArray(val)) {
          if (typeof val[country] === 'number') return val[country]
          if (altCountry && typeof val[altCountry] === 'number') return val[altCountry]
          return 0
        }
        if (Array.isArray(val)) {
          const list = val.map((c) => String(c).toLowerCase())
          return list.includes(country) || (altCountry && list.includes(altCountry)) ? 1 : 0
        }
        return 0
      }
      if (keyStr === 'time_zone_distribution' || keyStr === 'time_zone_distribution_total') {
        const val = firstData.user?.time_zone_distribution
        if (typeof val === 'number') return val
        if (val && typeof val === 'object') {
          if (typeof val.total === 'number') return val.total
          return Object.entries(val).reduce(
            (s, [k, v]) => s + (k !== '_id' && typeof v === 'number' ? v : 0),
            0
          )
        }
        return 0
      }
      if (keyStr.startsWith('time_zone_distribution_')) {
        const targetTz = dataItem?.rawTz || dataItem?.value
        const val = firstData.user?.time_zone_distribution
        if (val && typeof val === 'object') {
          if (targetTz && typeof val[targetTz] === 'number') return val[targetTz]
          for (const [k, v] of Object.entries(val)) {
            if (k !== 'total' && k !== '_id' && typeof v === 'number') {
              if (`time_zone_distribution_${k.replace(/[^a-zA-Z0-9]/g, '_')}` === keyStr) {
                return v
              }
            }
          }
        }
        return 0
      }

      // Monetization (revenue, monster_mint, nft_repair, shop boxes, guild, etc.)
      const matchMonetization = keyStr.match(/^(revenue|monster_mint|nft_repair|genesis_box|general_box|farm|bit|guild|change_guild)_(fiat|oas|count|average_per_user)$/i)
      if (matchMonetization) {
        const subCat = matchMonetization[1].toLowerCase()
        const metric = matchMonetization[2].toLowerCase()
        const subObj = firstData.monetization?.[subCat] || firstData.monetization?.shop?.[subCat]
        const val = subObj?.[metric]
        if (typeof val === 'number') return val
      }

      // Item (Items 128 - 131) - Fast direct resolve
      if (keyStr.startsWith('item_')) {
        if (keyStr === 'item_mint_count' || keyStr === 'item_mint_count_total') {
          const val = firstData.item?.item_mint_count?.total ?? (typeof firstData.item?.item_mint_count === 'number' ? firstData.item?.item_mint_count : 0)
          if (typeof val === 'number') return val
        }
        if (keyStr === 'item_used_count' || keyStr === 'item_used_count_total') {
          const val = firstData.item?.item_used_count?.total ?? (typeof firstData.item?.item_used_count === 'number' ? firstData.item?.item_used_count : 0)
          if (typeof val === 'number') return val
        }
        if (keyStr === 'item_distribution' || keyStr === 'item_distribution_total') {
          const val = firstData.item?.item_distribution?.total ?? (typeof firstData.item?.item_distribution === 'number' ? firstData.item?.item_distribution : 0)
          if (typeof val === 'number') return val
          const obj = firstData.item?.item_distribution
          if (obj && typeof obj === 'object') {
            return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
          }
        }
        if (keyStr === 'item_acquisition' || keyStr === 'item_acquisition_total') {
          const val = firstData.item?.item_acquisition?.total ?? (typeof firstData.item?.item_acquisition === 'number' ? firstData.item?.item_acquisition : 0)
          if (typeof val === 'number') return val
          const obj = firstData.item?.item_acquisition
          if (obj && typeof obj === 'object') {
            return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
          }
        }
        if (keyStr === 'item_acquisition_tournament') {
          const val = firstData.item?.item_acquisition?.tournament?.total ?? firstData.item?.item_acquisition?.tournament
          if (typeof val === 'number') return val
        }
        if (keyStr === 'item_acquisition_exploration') {
          const val = firstData.item?.item_acquisition?.exploration?.total ?? firstData.item?.item_acquisition?.exploration
          if (typeof val === 'number') return val
        }
        if (keyStr === 'item_acquisition_prize_rank_match' || keyStr === 'item_acquisition_prized_battle') {
          const val = firstData.item?.item_acquisition?.prize_rank_match?.total ?? firstData.item?.item_acquisition?.prized_battle?.total ?? firstData.item?.item_acquisition?.prized_battle
          if (typeof val === 'number') return val
        }
        if (keyStr === 'item_acquisition_shop') {
          const val = firstData.item?.item_acquisition?.shop
          if (typeof val === 'number') return val
        }
        if (keyStr === 'item_acquisition_login_bonus') {
          const val = firstData.item?.item_acquisition?.login_bonus
          if (typeof val === 'number') return val
        }
        if (keyStr === 'item_acquisition_other') {
          const val = firstData.item?.item_acquisition?.other
          if (typeof val === 'number') return val
        }
      }

      // Game Play - Login Days: number_of_login_days_(total|average)
      if (keyStr === 'number_of_login_days_total') {
        const val = firstData.game_play?.number_of_login_days?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'number_of_login_days_average') {
        const val = firstData.game_play?.number_of_login_days?.average
        if (typeof val === 'number') return val
      }

      // Game Play - Player Rank: player_rank_(beginner|intermediate|advance|expert|master|legend)
      const matchPlayerRank = keyStr.match(/^player_rank_(beginner|intermediate|advance|expert|master|legend)$/i)
      if (matchPlayerRank) {
        const rank = matchPlayerRank[1].toLowerCase()
        const val = firstData.game_play?.player_rank?.[rank]
        if (typeof val === 'number') return val
      }

      // Game Play - Time Passed in Game: time_passed_in_game_(total|average)
      if (keyStr === 'time_passed_in_game_total') {
        const val = firstData.game_play?.time_passed_in_game?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'time_passed_in_game_average') {
        const val = firstData.game_play?.time_passed_in_game?.average
        if (typeof val === 'number') return val
      }

      // Game Play - Chosen Meal: chosen_meal_(none|vegetable|meat|fish|supplement|dessert)
      const matchMeal = keyStr.match(/^chosen_meal_(none|vegetable|meat|fish|supplement|dessert)$/i)
      if (matchMeal) {
        const meal = matchMeal[1].toLowerCase()
        const val = firstData.game_play?.chosen_meal?.[meal]
        if (typeof val === 'number') return val
      }

      // Game Play - Rest & Hospital: rest_count_(total|average), hospital_count_(total|average)
      if (keyStr === 'rest_count_total') {
        const val = firstData.game_play?.rest_count?.total ?? (typeof firstData.game_play?.rest_count === 'number' ? firstData.game_play?.rest_count : 0)
        if (typeof val === 'number') return val
      }
      if (keyStr === 'rest_count_average') {
        const val = firstData.game_play?.rest_count?.average
        if (typeof val === 'number') return val
      }
      if (keyStr === 'hospital_count_total') {
        const val = firstData.game_play?.hospital_count?.total ?? (typeof firstData.game_play?.hospital_count === 'number' ? firstData.game_play?.hospital_count : 0)
        if (typeof val === 'number') return val
      }
      if (keyStr === 'hospital_count_average') {
        const val = firstData.game_play?.hospital_count?.average
        if (typeof val === 'number') return val
      }

      // Monster - Monster Rank: monster_rank_([a-zs])
      const matchMonsterRank = keyStr.match(/^monster_rank_([a-zs])$/i)
      if (matchMonsterRank) {
        const rank = matchMonsterRank[1].toLowerCase()
        const val = firstData.monster?.monster_rank?.[rank]
        if (typeof val === 'number') return val
      }

      // Regeneration - Mint counts: nft_mint_count, collab_nft_mint_count
      if (keyStr === 'nft_mint_count') {
        const val = firstData.regeneration?.nft_mint_count
        if (typeof val === 'number') return val
      }
      if (keyStr === 'collab_nft_mint_count') {
        const val = firstData.regeneration?.collab_nft_mint_count
        if (typeof val === 'number') return val
      }

      const match = keyStr.match(/^(tournament_battle_count|tournament_battle_win_count|tournament_battle_loss_count)_([a-zs])_(total|average)$/i)
      if (match) {
        const metricName = match[1]
        const groupLetter = match[2].toLowerCase()
        const statType = match[3]
        const val = firstData.game_play?.[metricName]?.[groupLetter]?.[statType]
        if (typeof val === 'number') return val
      }

      // Training Count: training_count_(hp|str|int|dex|agi|vit)_(total|average) or training_count_(hp|str|int|dex|agi|vit)
      const matchTraining = keyStr.match(/^training_count_(hp|str|int|dex|agi|vit)(?:_(total|average))?$/i)
      if (matchTraining) {
        const stat = matchTraining[1].toLowerCase()
        const metricType = (matchTraining[2] || 'total').toLowerCase()
        const val = firstData.game_play?.training_count?.[stat]?.[metricType]
        if (typeof val === 'number') return val
        if (firstData.game_play?.training_count?.[stat]?.total !== undefined) {
          return firstData.game_play.training_count[stat].total
        }
      }
      if (keyStr === 'training_count') {
        const obj = firstData.game_play?.training_count
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v?.total === 'number' ? v.total : 0), 0)
        }
      }

      // Intensive Training Count: intensive_training_count_([a-z_]+)_(total|average) or intensive_training_count_([a-z_]+)
      const matchIntensiveTraining = keyStr.match(/^intensive_training_count_([a-z_]+?)(?:_(total|average))?$/i)
      if (matchIntensiveTraining) {
        const mapName = matchIntensiveTraining[1].toLowerCase()
        const metricType = (matchIntensiveTraining[2] || 'total').toLowerCase()
        const val = firstData.game_play?.intensive_training_count?.[mapName]?.[metricType]
        if (typeof val === 'number') return val
        if (firstData.game_play?.intensive_training_count?.[mapName]?.total !== undefined) {
          return firstData.game_play.intensive_training_count[mapName].total
        }
      }
      if (keyStr === 'intensive_training_count') {
        const obj = firstData.game_play?.intensive_training_count
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v?.total === 'number' ? v.total : 0), 0)
        }
      }

      // Ranked Match Battle Count: ranked_match_battle_count_(simple|prize)_(total|average)
      const matchBattleCount = keyStr.match(/^ranked_match_battle_count_(simple|prize)_(total|average)$/i)
      if (matchBattleCount) {
        const type = matchBattleCount[1].toLowerCase()
        const stat = matchBattleCount[2].toLowerCase()
        const val = firstData.game_play?.ranked_match_battle_count?.[type]?.[stat]
        if (typeof val === 'number') return val
      }

      // Ranked Match Selection Rank: ranked_match_selection_rank_(simple|prize)_([a-zs])$
      const matchSelectionRank = keyStr.match(/^ranked_match_selection_rank_(simple|prize)_([a-zs])$/i)
      if (matchSelectionRank) {
        const type = matchSelectionRank[1].toLowerCase()
        const rank = matchSelectionRank[2].toLowerCase()
        const val = firstData.game_play?.ranked_match_selection_rank?.[type]?.[rank]
        if (typeof val === 'number') return val
      }

      // Ranked Match Battle Win/Loss: ranked_match_battle_win_loss_(simple|prize)_(win|loss)_(total|average)$
      const matchWinLoss = keyStr.match(/^ranked_match_battle_win_loss_(simple|prize)_(win|loss)_(total|average)$/i)
      if (matchWinLoss) {
        const type = matchWinLoss[1].toLowerCase()
        const outcome = matchWinLoss[2].toLowerCase()
        const stat = matchWinLoss[3].toLowerCase()
        const val = firstData.game_play?.ranked_match_battle_win_loss?.[type]?.[outcome]?.[stat]
        if (typeof val === 'number') return val
      }

      // Exploration Count: exploration_count_(dustblade|...|lostland)_(total|average)
      const matchExploration = keyStr.match(/^exploration_count_([a-z_]+)_(total|average)$/i)
      if (matchExploration) {
        const mapName = matchExploration[1].toLowerCase()
        const stat = matchExploration[2].toLowerCase()
        const val = firstData.game_play?.exploration_count?.[mapName]?.[stat]
        if (typeof val === 'number') return val
      }

      // Amount of Items Found During Exploration: amount_of_items_found_during_exploration_(map)_(item)_(total|average)
      const mapNames = 'dustblade|whispwood|mystic_forest|skypeak|windland|greenfield|ash_valley|silver_lake|mirage_cove|lostland'
      const matchItemFound = keyStr.match(new RegExp(`^amount_of_items_found_during_exploration_(${mapNames})_(.+)_(total|average)$`, 'i'))
      if (matchItemFound) {
        const mapName = matchItemFound[1].toLowerCase()
        const itemKey = matchItemFound[2].toLowerCase()
        const stat = matchItemFound[3].toLowerCase()
        const mapData = firstData.game_play?.amount_of_items_found_during_exploration?.[mapName]
        if (mapData) {
          const matchedItemProp = Object.keys(mapData).find((k) => k.toLowerCase() === itemKey)
          const val = matchedItemProp ? mapData[matchedItemProp]?.[stat] : 0
          if (typeof val === 'number') return val
        }
      }

      // In-Game Currency Acquisition: bit_earned_(total|average) or bit_earned_useage_(...)
      if (keyStr === 'bit_earned_total') {
        const val = firstData.game_play?.bit_earned?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'bit_earned_average') {
        const val = firstData.game_play?.bit_earned?.average
        if (typeof val === 'number') return val
      }
      const matchBitEarnedUseage = keyStr.match(/^bit_earned_useage_(.+)$/i)
      if (matchBitEarnedUseage) {
        const useKey = matchBitEarnedUseage[1].toLowerCase()
        const val = firstData.game_play?.bit_earned?.useage?.[useKey]
        if (typeof val === 'number') return val
      }

      // In-Game Currency Usage: bit_used_(total|average) or bit_used_useage_(...)
      if (keyStr === 'bit_used_total') {
        const val = firstData.game_play?.bit_used?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'bit_used_average') {
        const val = firstData.game_play?.bit_used?.average
        if (typeof val === 'number') return val
      }
      const matchBitUsedUseage = keyStr.match(/^bit_used_useage_(.+)$/i)
      if (matchBitUsedUseage) {
        const useKey = matchBitUsedUseage[1].toLowerCase()
        const val = firstData.game_play?.bit_used?.useage?.[useKey]
        if (typeof val === 'number') return val
      }

      // Regeneration Ticket Acquisition: regeneration_ticket_earned_(total|average) or regeneration_ticket_earned_useage_(...)
      if (keyStr === 'regeneration_ticket_earned_total') {
        const val = firstData.game_play?.regeneration_ticket_earned?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'regeneration_ticket_earned_average') {
        const val = firstData.game_play?.regeneration_ticket_earned?.average
        if (typeof val === 'number') return val
      }
      const matchRegenTicketUseage = keyStr.match(/^regeneration_ticket_earned_useage_(.+)$/i)
      if (matchRegenTicketUseage) {
        const useKey = matchRegenTicketUseage[1].toLowerCase()
        const val = firstData.game_play?.regeneration_ticket_earned?.useage?.[useKey]
        if (typeof val === 'number') return val
      }

      // Regeneration Ticket Usage: regeneration_ticket_used_(total|average) or regeneration_ticket_used_useage_(...)
      if (keyStr === 'regeneration_ticket_used_total') {
        const val = firstData.game_play?.regeneration_ticket_used?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'regeneration_ticket_used_average') {
        const val = firstData.game_play?.regeneration_ticket_used?.average
        if (typeof val === 'number') return val
      }
      const matchRegenUsedUseage = keyStr.match(/^regeneration_ticket_used_useage_(.+)$/i)
      if (matchRegenUsedUseage) {
        const useKey = matchRegenUsedUseage[1].toLowerCase()
        const val = firstData.game_play?.regeneration_ticket_used?.useage?.[useKey]
        if (typeof val === 'number') return val
      }

      // Tournament Ticket Acquisition: tournament_ticket_earned_(total|average) or tournament_ticket_earned_useage_(...)
      if (keyStr === 'tournament_ticket_earned_total') {
        const val = firstData.game_play?.tournament_ticket_earned?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'tournament_ticket_earned_average') {
        const val = firstData.game_play?.tournament_ticket_earned?.average
        if (typeof val === 'number') return val
      }
      const matchTournEarnedUseage = keyStr.match(/^tournament_ticket_earned_useage_(.+)$/i)
      if (matchTournEarnedUseage) {
        const useKey = matchTournEarnedUseage[1].toLowerCase()
        const val = firstData.game_play?.tournament_ticket_earned?.useage?.[useKey]
        if (typeof val === 'number') return val
      }

      // Tournament Ticket Usage: tournament_ticket_used_(total|average) or tournament_ticket_used_useage_(...)
      if (keyStr === 'tournament_ticket_used_total') {
        const val = firstData.game_play?.tournament_ticket_used?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'tournament_ticket_used_average') {
        const val = firstData.game_play?.tournament_ticket_used?.average
        if (typeof val === 'number') return val
      }
      const matchTournUsedUseage = keyStr.match(/^tournament_ticket_used_useage_(.+)$/i)
      if (matchTournUsedUseage) {
        const useKey = matchTournUsedUseage[1].toLowerCase()
        const val = firstData.game_play?.tournament_ticket_used?.useage?.[useKey]
        if (typeof val === 'number') return val
      }

      // Exploration Ticket Acquisition: exploration_ticket_earned_(total|average) or exploration_ticket_earned_useage_(...)
      if (keyStr === 'exploration_ticket_earned_total') {
        const val = firstData.game_play?.exploration_ticket_earned?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'exploration_ticket_earned_average') {
        const val = firstData.game_play?.exploration_ticket_earned?.average
        if (typeof val === 'number') return val
      }
      const matchExplEarnedUseage = keyStr.match(/^exploration_ticket_earned_useage_(.+)$/i)
      if (matchExplEarnedUseage) {
        const useKey = matchExplEarnedUseage[1].toLowerCase()
        const val = firstData.game_play?.exploration_ticket_earned?.useage?.[useKey]
        if (typeof val === 'number') return val
      }

      // Exploration Ticket Usage: exploration_ticket_used_(total|average) or exploration_ticket_used_useage_(...)
      if (keyStr === 'exploration_ticket_used_total') {
        const val = firstData.game_play?.exploration_ticket_used?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'exploration_ticket_used_average') {
        const val = firstData.game_play?.exploration_ticket_used?.average
        if (typeof val === 'number') return val
      }
      const matchExplUsedUseage = keyStr.match(/^exploration_ticket_used_useage_(.+)$/i)
      if (matchExplUsedUseage) {
        const useKey = matchExplUsedUseage[1].toLowerCase()
        const val = firstData.game_play?.exploration_ticket_used?.useage?.[useKey]
        if (typeof val === 'number') return val
      }

      // Regeneration - NFT Mint: nft_mint_total, nft_mint_average
      if (keyStr === 'nft_mint_total') {
        const val = firstData.regeneration?.nft_mint?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'nft_mint_average') {
        const val = firstData.regeneration?.nft_mint?.average
        if (typeof val === 'number') return val
      }

      // Regeneration - NFT Mint By Chain: nft_mint_chain_(oasys|polygon|astar|ethereum|arbitrum|mch)_(total|average)
      const matchNftMintChain = keyStr.match(/^nft_mint_chain_([a-z0-9_]+)_(total|average)$/i)
      if (matchNftMintChain) {
        const chain = matchNftMintChain[1].toLowerCase()
        const stat = matchNftMintChain[2].toLowerCase()
        const val = firstData.regeneration?.nft_mint?.by_chain?.[chain]?.[stat]
        if (typeof val === 'number') return val
      }

      // Regeneration - NFT Mint By Contract: nft_mint_contract_(0x[a-f0-9]+)_(total|average)
      const matchNftMintContract = keyStr.match(/^nft_mint_contract_(0x[a-f0-9]+)_(total|average)$/i)
      if (matchNftMintContract) {
        const contract = matchNftMintContract[1].toLowerCase()
        const stat = matchNftMintContract[2].toLowerCase()
        const val = firstData.regeneration?.nft_mint?.by_contract?.[contract]?.[stat]
        if (typeof val === 'number') return val
      }

      // Regeneration - Free Mint: free_mint_total, free_mint_average
      if (keyStr === 'free_mint_total') {
        const val = firstData.regeneration?.free_mint?.total
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.free_mint_count
        if (typeof fallback === 'number') return fallback
      }
      if (keyStr === 'free_mint_average') {
        const val = firstData.regeneration?.free_mint?.average
        if (typeof val === 'number') return val
      }

      // Regeneration - Regeneration Hash: regeneration_hash_total, regeneration_hash_average
      if (keyStr === 'regeneration_hash_total') {
        const val = firstData.regeneration?.regeneration_hash?.total
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.regeneration_hash_mint_count
        if (typeof fallback === 'number') return fallback
      }
      if (keyStr === 'regeneration_hash_average') {
        const val = firstData.regeneration?.regeneration_hash?.average
        if (typeof val === 'number') return val
      }

      // Regeneration - Regeneration Hash By Species: regeneration_hash_species_(...)
      const matchRegenHashSpecies = keyStr.match(/^regeneration_hash_species_(.+)$/i)
      if (matchRegenHashSpecies) {
        const spKey = matchRegenHashSpecies[1].toLowerCase()
        const bySpecies = firstData.regeneration?.regeneration_hash?.by_species
        if (bySpecies) {
          const matchedKey = Object.keys(bySpecies).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? bySpecies[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Regeneration - Genesis Hash: genesis_hash_total, genesis_hash_average
      if (keyStr === 'genesis_hash_total') {
        const val = firstData.regeneration?.genesis_hash?.total
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.genesis_mint_count
        if (typeof fallback === 'number') return fallback
      }
      if (keyStr === 'genesis_hash_average') {
        const val = firstData.regeneration?.genesis_hash?.average
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.avg_mint_count_genesis
        if (typeof fallback === 'number') return fallback
      }

      // Regeneration - Genesis Hash By Species: genesis_hash_species_(...)
      const matchGenesisHashSpecies = keyStr.match(/^genesis_hash_species_(.+)$/i)
      if (matchGenesisHashSpecies) {
        const spKey = matchGenesisHashSpecies[1].toLowerCase()
        const bySpecies = firstData.regeneration?.genesis_hash?.by_species
        if (bySpecies) {
          const matchedKey = Object.keys(bySpecies).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? bySpecies[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Regeneration - General Hash: general_hash_total, general_hash_average
      if (keyStr === 'general_hash_total') {
        const val = firstData.regeneration?.general_hash?.total
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.general_mint_count
        if (typeof fallback === 'number') return fallback
      }
      if (keyStr === 'general_hash_average') {
        const val = firstData.regeneration?.general_hash?.average
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.avg_mint_count_general
        if (typeof fallback === 'number') return fallback
      }

      // Regeneration - General Hash By Species: general_hash_species_(...)
      const matchGeneralHashSpecies = keyStr.match(/^general_hash_species_(.+)$/i)
      if (matchGeneralHashSpecies) {
        const spKey = matchGeneralHashSpecies[1].toLowerCase()
        const bySpecies = firstData.regeneration?.general_hash?.by_species
        if (bySpecies) {
          const matchedKey = Object.keys(bySpecies).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? bySpecies[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Regeneration - Hash Chip NFT: hash_chip_nft_total, hash_chip_nft_average, hash_chip_mint_count
      if (keyStr === 'hash_chip_nft_total' || keyStr === 'hash_chip_mint_count') {
        const val = firstData.regeneration?.hash_chip_nft?.total
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.hash_chip_mint_count
        if (typeof fallback === 'number') return fallback
      }
      if (keyStr === 'hash_chip_nft_average') {
        const val = firstData.regeneration?.hash_chip_nft?.average
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.avg_mint_count_hash_chip
        if (typeof fallback === 'number') return fallback
      }

      // Regeneration - Hash Chip NFT By Species: hash_chip_nft_species_(...)
      const matchHashChipSpecies = keyStr.match(/^hash_chip_nft_species_(.+)$/i)
      if (matchHashChipSpecies) {
        const spKey = matchHashChipSpecies[1].toLowerCase()
        const bySpecies = firstData.regeneration?.hash_chip_nft?.by_species
        if (bySpecies) {
          const matchedKey = Object.keys(bySpecies).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? bySpecies[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Regeneration - Average number of mints from each NFT and $: each_nft_mint_average, each_nft_mint_fiat, each_nft_mint_oas
      if (keyStr === 'each_nft_mint_average') {
        const val = firstData.regeneration?.each_nft_mint?.average_mint_count
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.average_mint_count_from_each_nft
        if (typeof fallback === 'number') return fallback
      }
      if (keyStr === 'each_nft_mint_fiat') {
        const val = firstData.regeneration?.each_nft_mint?.fiat
        if (typeof val === 'number') return val
      }
      if (keyStr === 'each_nft_mint_oas') {
        const val = firstData.regeneration?.each_nft_mint?.oas
        if (typeof val === 'number') return val
      }

      // Regeneration - Each NFT Mint By Cycle: each_nft_mint_cycle_(1st|2nd|3rd|4th_plus)_(count|fiat|oas)
      const matchEachNftCycle = keyStr.match(/^each_nft_mint_cycle_(1st|2nd|3rd|4th_plus)_(count|fiat|oas)$/i)
      if (matchEachNftCycle) {
        const cycle = `mint_${matchEachNftCycle[1].toLowerCase()}`
        const metric = matchEachNftCycle[2].toLowerCase()
        const val = firstData.regeneration?.each_nft_mint?.by_cycle?.[cycle]?.[metric]
        if (typeof val === 'number') return val
      }

      // Regeneration - Each NFT Mint By Species: each_nft_mint_species_(...)
      const matchEachNftSpecies = keyStr.match(/^each_nft_mint_species_(.+)$/i)
      if (matchEachNftSpecies) {
        const spKey = matchEachNftSpecies[1].toLowerCase()
        const bySpecies = firstData.regeneration?.each_nft_mint?.by_species
        if (bySpecies) {
          const matchedKey = Object.keys(bySpecies).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? bySpecies[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Regeneration - Average number of mints and $ from Genesis Hash: genesis_hash_mint_average, genesis_hash_mint_fiat, genesis_hash_mint_oas
      if (keyStr === 'genesis_hash_mint_average') {
        const val = firstData.regeneration?.genesis_hash_mint?.average_mint_count
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.average_mint_count_from_genesis_hash
        if (typeof fallback === 'number') return fallback
      }
      if (keyStr === 'genesis_hash_mint_fiat') {
        const val = firstData.regeneration?.genesis_hash_mint?.fiat
        if (typeof val === 'number') return val
      }
      if (keyStr === 'genesis_hash_mint_oas') {
        const val = firstData.regeneration?.genesis_hash_mint?.oas
        if (typeof val === 'number') return val
      }

      // Regeneration - Genesis Hash Mint By Cycle: genesis_hash_mint_cycle_(1st|2nd|3rd|4th|5th|6th_plus)_(count|fiat|oas)
      const matchGenesisNftCycle = keyStr.match(/^genesis_hash_mint_cycle_(1st|2nd|3rd|4th|5th|6th_plus)_(count|fiat|oas)$/i)
      if (matchGenesisNftCycle) {
        const cycle = `mint_${matchGenesisNftCycle[1].toLowerCase()}`
        const metric = matchGenesisNftCycle[2].toLowerCase()
        const val = firstData.regeneration?.genesis_hash_mint?.by_cycle?.[cycle]?.[metric]
        if (typeof val === 'number') return val
      }

      // Regeneration - Genesis Hash Mint By Species: genesis_hash_mint_species_(...)
      const matchGenesisNftSpecies = keyStr.match(/^genesis_hash_mint_species_(.+)$/i)
      if (matchGenesisNftSpecies) {
        const spKey = matchGenesisNftSpecies[1].toLowerCase()
        const bySpecies = firstData.regeneration?.genesis_hash_mint?.by_species
        if (bySpecies) {
          const matchedKey = Object.keys(bySpecies).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? bySpecies[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Regeneration - F Number of Mints from Regeneration: f_regeneration_mint_total, f_regeneration_mint_average
      if (keyStr === 'f_regeneration_mint_total') {
        const val = firstData.regeneration?.f_regeneration_mint?.total
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.mint_count_from_f_regeneration ?? firstData.regeneration?.fusion_regeneration_mint_count
        if (typeof fallback === 'number') return fallback
      }
      if (keyStr === 'f_regeneration_mint_average') {
        const val = firstData.regeneration?.f_regeneration_mint?.average
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.avg_mint_count_fusion_regeneration
        if (typeof fallback === 'number') return fallback
      }

      // Regeneration - F Regeneration Mint By Species: f_regeneration_mint_species_(...)
      const matchFRegenSpecies = keyStr.match(/^f_regeneration_mint_species_(.+)$/i)
      if (matchFRegenSpecies) {
        const spKey = matchFRegenSpecies[1].toLowerCase()
        const bySpecies = firstData.regeneration?.f_regeneration_mint?.by_species
        if (bySpecies) {
          const matchedKey = Object.keys(bySpecies).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? bySpecies[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Regeneration - Average number of mints from F Regeneration and $: f_regeneration_avg_mint_average, f_regeneration_avg_mint_fiat, f_regeneration_avg_mint_oas
      if (keyStr === 'f_regeneration_avg_mint_average') {
        const val = firstData.regeneration?.f_regeneration_avg_mint?.average_mint_count
        if (typeof val === 'number') return val
        const fallback = firstData.regeneration?.average_mint_count_and_from_f_regeneration
        if (typeof fallback === 'number') return fallback
      }
      if (keyStr === 'f_regeneration_avg_mint_fiat') {
        const val = firstData.regeneration?.f_regeneration_avg_mint?.fiat
        if (typeof val === 'number') return val
      }
      if (keyStr === 'f_regeneration_avg_mint_oas') {
        const val = firstData.regeneration?.f_regeneration_avg_mint?.oas
        if (typeof val === 'number') return val
      }

      // Regeneration - F Regeneration Mint By Cycle: f_regeneration_avg_mint_cycle_(1st|2nd|3rd|4th|5th|6th_plus)_(count|fiat|oas)
      const matchFRegenCycle = keyStr.match(/^f_regeneration_avg_mint_cycle_(1st|2nd|3rd|4th|5th|6th_plus)_(count|fiat|oas)$/i)
      if (matchFRegenCycle) {
        const cycle = `mint_${matchFRegenCycle[1].toLowerCase()}`
        const metric = matchFRegenCycle[2].toLowerCase()
        const val = firstData.regeneration?.f_regeneration_avg_mint?.by_cycle?.[cycle]?.[metric]
        if (typeof val === 'number') return val
      }

      // Regeneration - F Regeneration Avg Mint By Species: f_regeneration_avg_mint_species_(...)
      const matchFRegenAvgSpecies = keyStr.match(/^f_regeneration_avg_mint_species_(.+)$/i)
      if (matchFRegenAvgSpecies) {
        const spKey = matchFRegenAvgSpecies[1].toLowerCase()
        const bySpecies = firstData.regeneration?.f_regeneration_avg_mint?.by_species
        if (bySpecies) {
          const matchedKey = Object.keys(bySpecies).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? bySpecies[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Regeneration - Types of Monsters that were born By Main Seed: born_monster_types_main_(...)
      const matchBornMain = keyStr.match(/^born_monster_types_main_(.+)$/i)
      if (matchBornMain) {
        const spKey = matchBornMain[1].toLowerCase()
        const byMain = firstData.regeneration?.born_monster_types?.by_main_seed
        if (byMain) {
          const matchedKey = Object.keys(byMain).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? byMain[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Regeneration - Types of Monsters that were born By Sub Seed: born_monster_types_sub_(...)
      const matchBornSub = keyStr.match(/^born_monster_types_sub_(.+)$/i)
      if (matchBornSub) {
        const spKey = matchBornSub[1].toLowerCase()
        const bySub = firstData.regeneration?.born_monster_types?.by_sub_seed
        if (bySub) {
          const matchedKey = Object.keys(bySub).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? bySub[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Fusion - 68. Fusion Count: fusion_count_total, fusion_count_average
      if (keyStr === 'fusion_count_total') {
        const val = firstData.fusion?.fusion_count?.total ?? firstData.fusion?.fusion_count
        if (typeof val === 'number') return val
      }
      if (keyStr === 'fusion_count_average') {
        const val = firstData.fusion?.fusion_count?.average
        if (typeof val === 'number') return val
      }

      // Fusion - 69. Base Monster Types: base_monster_types_total, base_monster_types_average, base_monster_types_main_(...), base_monster_types_sub_(...)
      if (keyStr === 'base_monster_types_total') {
        const val = firstData.fusion?.base_monster_types?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'base_monster_types_average') {
        const val = firstData.fusion?.base_monster_types?.average
        if (typeof val === 'number') return val
      }
      const matchBaseMain = keyStr.match(/^base_monster_types_main_(.+)$/i)
      if (matchBaseMain) {
        const spKey = matchBaseMain[1].toLowerCase()
        const byMain = firstData.fusion?.base_monster_types?.by_main_seed
        if (byMain) {
          const matchedKey = Object.keys(byMain).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? byMain[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }
      const matchBaseSub = keyStr.match(/^base_monster_types_sub_(.+)$/i)
      if (matchBaseSub) {
        const spKey = matchBaseSub[1].toLowerCase()
        const bySub = firstData.fusion?.base_monster_types?.by_sub_seed
        if (bySub) {
          const matchedKey = Object.keys(bySub).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? bySub[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Fusion - 70. Born Monster Types: fusion_born_monster_types_total, fusion_born_monster_types_main_(...), fusion_born_monster_types_sub_(...)
      if (keyStr === 'fusion_born_monster_types_total') {
        const val = firstData.fusion?.born_monster_types?.total
        if (typeof val === 'number') return val
      }
      const matchFusionBornMain = keyStr.match(/^fusion_born_monster_types_main_(.+)$/i)
      if (matchFusionBornMain) {
        const spKey = matchFusionBornMain[1].toLowerCase()
        const byMain = firstData.fusion?.born_monster_types?.by_main_seed
        if (byMain) {
          const matchedKey = Object.keys(byMain).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? byMain[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }
      const matchFusionBornSub = keyStr.match(/^fusion_born_monster_types_sub_(.+)$/i)
      if (matchFusionBornSub) {
        const spKey = matchFusionBornSub[1].toLowerCase()
        const bySub = firstData.fusion?.born_monster_types?.by_sub_seed
        if (bySub) {
          const matchedKey = Object.keys(bySub).find((k) => k.toLowerCase() === spKey)
          const val = matchedKey ? bySub[matchedKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Fusion - 71. Lifespan Fluctuation: fusion_lifespan_fluctuation_percentage
      if (keyStr === 'fusion_lifespan_fluctuation_percentage') {
        const val = firstData.fusion?.lifespan_fluctuation?.average_percentage ?? firstData.fusion?.avg_change_of_lifespan
        if (typeof val === 'number') return val
      }

      // Fusion - 72. Average Basic Parameter: fusion_avg_basic_param_(hp|str|int|dex|agi|vit)
      const matchBasicParam = keyStr.match(/^fusion_avg_basic_param_(hp|str|int|dex|agi|vit)$/i)
      if (matchBasicParam) {
        const pKey = matchBasicParam[1].toLowerCase()
        const val = firstData.fusion?.avg_basic_parameter?.[pKey]
        if (typeof val === 'number') return val
      }

      // Fusion - 73. Average Overall Ability: fusion_avg_overall_ability
      if (keyStr === 'fusion_avg_overall_ability') {
        const val = firstData.fusion?.avg_overall_ability
        if (typeof val === 'number') return val
      }

      // Fusion - 74. Maximum Overall Ability: fusion_max_overall_ability
      if (keyStr === 'fusion_max_overall_ability') {
        const val = firstData.fusion?.max_overall_ability
        if (typeof val === 'number') return val
      }

      // Fusion - 75. Fusion Items Used: fusion_items_used_total, fusion_items_used_average, fusion_items_used_type_(...)
      if (keyStr === 'fusion_items_used_total') {
        const val = firstData.fusion?.fusion_items_used?.total ?? firstData.fusion?.fusion_item_used?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'fusion_items_used_average') {
        const val = firstData.fusion?.fusion_items_used?.average
        if (typeof val === 'number') return val
      }
      if (keyStr === 'fusion_items_used_type_crystal_total') {
        const val = firstData.fusion?.fusion_items_used?.by_type?.crystal?.total ?? firstData.fusion?.fusion_item_used?.crystal
        if (typeof val === 'number') return val
      }
      if (keyStr === 'fusion_items_used_type_crystal_average') {
        const val = firstData.fusion?.fusion_items_used?.by_type?.crystal?.average
        if (typeof val === 'number') return val
      }
      if (keyStr === 'fusion_items_used_type_catalyst_total') {
        const val = firstData.fusion?.fusion_items_used?.by_type?.catalyst_item?.total ?? firstData.fusion?.fusion_item_used?.catalyst_item
        if (typeof val === 'number') return val
      }
      if (keyStr === 'fusion_items_used_type_catalyst_average') {
        const val = firstData.fusion?.fusion_items_used?.by_type?.catalyst_item?.average
        if (typeof val === 'number') return val
      }
      if (keyStr === 'fusion_items_used_type_life_potion_total') {
        const val = firstData.fusion?.fusion_items_used?.by_type?.life_potion?.total ?? firstData.fusion?.fusion_item_used?.life_potion
        if (typeof val === 'number') return val
      }
      if (keyStr === 'fusion_items_used_type_life_potion_average') {
        const val = firstData.fusion?.fusion_items_used?.by_type?.life_potion?.average
        if (typeof val === 'number') return val
      }

      // Monster - 76. Total Monster: total_monster_total, total_monster_average
      if (keyStr === 'total_monster_total') {
        const val = firstData.monster?.total_monster?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'total_monster_average') {
        const val = firstData.monster?.total_monster?.average
        if (typeof val === 'number') return val
      }

      // Monster - 77. Current Monster: current_monster_total, current_monster_average
      if (keyStr === 'current_monster_total') {
        const val = firstData.monster?.current_monster?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'current_monster_average') {
        const val = firstData.monster?.current_monster?.average
        if (typeof val === 'number') return val
      }

      // Monster - 78. Current Memory: current_memory_total, current_memory_average
      if (keyStr === 'current_memory_total') {
        const val = firstData.monster?.current_memory?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'current_memory_average') {
        const val = firstData.monster?.current_memory?.average
        if (typeof val === 'number') return val
      }

      // Monster - 79. Monster Training Completed: monster_training_completed_total, monster_training_completed_average, monster_training_completed_percentage
      if (keyStr === 'monster_training_completed_total') {
        const val = firstData.monster?.monster_training_completed?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'monster_training_completed_average') {
        const val = firstData.monster?.monster_training_completed?.average
        if (typeof val === 'number') return val
      }
      if (keyStr === 'monster_training_completed_percentage') {
        const val = firstData.monster?.monster_training_completed?.percentage
        if (typeof val === 'number') return val
      }

      // Monster - 80. Used in rank match: used_in_rank_match
      if (keyStr === 'used_in_rank_match') {
        const val = firstData.monster?.used_in_rank_match ?? firstData.monster?.used_in_rank_match_count
        if (typeof val === 'number') return val
        if (val && typeof val.total === 'number') return val.total
      }

      // Monster - 81. Win / Lose in rank match: win_rank_match, lose_rank_match
      if (keyStr === 'win_rank_match') {
        const val = firstData.monster?.win_rank_match
        if (typeof val === 'number') return val
        if (val && typeof val.total === 'number') return val.total
        if (val && typeof val === 'object') {
          return Object.values(val).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }
      if (keyStr === 'lose_rank_match') {
        const val = firstData.monster?.lose_rank_match
        if (typeof val === 'number') return val
        if (val && typeof val.total === 'number') return val.total
        if (val && typeof val === 'object') {
          return Object.values(val).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Monster - 82. Used in tournament: used_in_tournament
      if (keyStr === 'used_in_tournament') {
        const val = firstData.monster?.used_in_tournament
        if (typeof val === 'number') return val
        if (val && typeof val.total === 'number') return val.total
        if (val && typeof val === 'object') {
          return Object.values(val).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Monster - 83. Win tournament: win_tournament
      if (keyStr === 'win_tournament') {
        const val = firstData.monster?.win_tournament
        if (typeof val === 'number') return val
        if (val && typeof val.total === 'number') return val.total
        if (val && typeof val === 'object') {
          return Object.values(val).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Monster - 85. Training item use: training_item_use
      if (keyStr === 'training_item_use') {
        const val = firstData.monster?.training_item_use
        if (typeof val === 'number') return val
        if (val && typeof val.total === 'number') return val.total
        if (val && typeof val === 'object') {
          return Object.values(val).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Monster - 86. Average Basic Parameter: average_basic_parameter, average_basic_parameter_(hp|str|int|dex|agi|vit)
      const matchMonsterBasicParam = keyStr.match(/^average_basic_parameter_(hp|str|int|dex|agi|vit)$/i)
      if (matchMonsterBasicParam) {
        const pKey = matchMonsterBasicParam[1].toLowerCase()
        const val = firstData.monster?.average_basic_parameter?.[pKey]
        if (typeof val === 'number') return val
      }
      if (keyStr === 'average_basic_parameter') {
        const val = firstData.monster?.average_basic_parameter
        if (typeof val === 'number') return val
        if (val && typeof val.hp === 'number') return val.hp
      }

      // Monster - 87. Average Overall: average_overall
      if (keyStr === 'average_overall') {
        const val = firstData.monster?.average_overall
        if (typeof val === 'number') return val
      }

      // Monster - 88. Maximum Overall: max_overall, max_basic_parameter
      if (keyStr === 'max_overall' || keyStr === 'max_overall_ability' || keyStr === 'max_basic_parameter') {
        const val = firstData.monster?.max_overall ?? firstData.monster?.max_overall_ability
        if (typeof val === 'number') return val
        if (val && typeof val.overall === 'number') return val.overall
      }

      // Monster - 89. Personality: personality, personality_(...)
      if (keyStr === 'personality') {
        const val = firstData.monster?.personality
        if (typeof val === 'number') return val
        if (val && typeof val === 'object') {
          return Object.values(val).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }
      const matchPersonality = keyStr.match(/^personality_(.+)$/i)
      if (matchPersonality) {
        const targetKey = matchPersonality[1].toLowerCase()
        const pObj = firstData.monster?.personality
        if (pObj && typeof pObj === 'object') {
          const foundKey = Object.keys(pObj).find((k) => k.toLowerCase() === targetKey)
          const val = foundKey ? pObj[foundKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Monster - 90. Innate Traits: innate_traits, innate_traits_(...)
      if (keyStr === 'innate_traits') {
        const val = firstData.monster?.innate_traits
        if (typeof val === 'number') return val
        if (val && typeof val === 'object') {
          return Object.values(val).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }
      const matchInnateTrait = keyStr.match(/^innate_traits_(.+)$/i)
      if (matchInnateTrait) {
        const targetKey = matchInnateTrait[1].toLowerCase()
        const tObj = firstData.monster?.innate_traits
        if (tObj && typeof tObj === 'object') {
          const foundKey = Object.keys(tObj).find((k) => k.toLowerCase() === targetKey)
          const val = foundKey ? tObj[foundKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Monster - 91. Acquired Traits: accquired_traits, acquired_traits, (accquired|acquired)_traits_(...)
      if (keyStr === 'accquired_traits' || keyStr === 'acquired_traits') {
        const val = firstData.monster?.accquired_traits ?? firstData.monster?.acquired_traits
        if (typeof val === 'number') return val
        if (val && typeof val === 'object') {
          return Object.values(val).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }
      const matchAcquiredTrait = keyStr.match(/^(accquired|acquired)_traits_(.+)$/i)
      if (matchAcquiredTrait) {
        const targetKey = matchAcquiredTrait[2].toLowerCase()
        const tObj = firstData.monster?.accquired_traits ?? firstData.monster?.acquired_traits
        if (tObj && typeof tObj === 'object') {
          const foundKey = Object.keys(tObj).find((k) => k.toLowerCase() === targetKey)
          const val = foundKey ? tObj[foundKey] : 0
          if (typeof val === 'number') return val
        }
      }

      // Monster - 92. Skill平均習得率 (Average skill acquisition rate: skill_1..6)
      const matchSkillAcquisition = keyStr.match(/^(average_skill_acquisition_rate|skill_acquisition_rate|skill_optain|skill_obtain)_(skill_[1-6])$/i)
      if (matchSkillAcquisition) {
        const skillKey = matchSkillAcquisition[2].toLowerCase()
        const acqRateObj = firstData.monster?.skill_acquisition_rate?.[skillKey]
        if (acqRateObj && typeof acqRateObj === 'object') {
          if (typeof acqRateObj.rate === 'number') return acqRateObj.rate
          if (typeof acqRateObj.total === 'number') return acqRateObj.total
        }
        const fallback = firstData.monster?.skill_optain?.[skillKey] ?? firstData.monster?.skill_obtain?.[skillKey]
        if (typeof fallback === 'number') return fallback
      }
      if (keyStr === 'average_skill_acquisition_rate' || keyStr === 'skill_acquisition_rate') {
        const obj = firstData.monster?.skill_acquisition_rate ?? firstData.monster?.skill_optain ?? firstData.monster?.skill_obtain
        if (obj && typeof obj === 'object') {
          const vals = Object.values(obj).map((v) =>
            v && typeof v === 'object' && typeof v.rate === 'number' ? v.rate : typeof v === 'number' ? v : 0
          )
          if (vals.length > 0) return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100
        }
      }

      // Monster - 93. 使用されているSkill (Skills used in battle: skill_1..6)
      const matchSkillsUsed = keyStr.match(/^(skills_used_in_battle|skills_used|skill_selected)_(skill_[1-6])$/i)
      if (matchSkillsUsed) {
        const skillKey = matchSkillsUsed[2].toLowerCase()
        const val = firstData.monster?.skill_selected?.[skillKey] ?? firstData.monster?.skills_used?.[skillKey]
        if (typeof val === 'number') return val
      }
      if (keyStr === 'skills_used_in_battle' || keyStr === 'skills_used' || keyStr === 'skill_selected') {
        const obj = firstData.monster?.skill_selected ?? firstData.monster?.skills_used
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Monster - 94. Terrain (Main và Sub: terrain_main_..., terrain_sub_...)
      const matchTerrain = keyStr.match(/^terrain_(main|sub)_(.+)$/i)
      if (matchTerrain) {
        const type = matchTerrain[1].toLowerCase() // main or sub
        const terrainKey = matchTerrain[2].toLowerCase()
        const tObj = firstData.monster?.terrain?.[`by_${type}`] ?? firstData.monster?.terrain?.[type]
        if (tObj && typeof tObj === 'object') {
          const foundKey = Object.keys(tObj).find((k) => {
            const kLower = k.toLowerCase()
            return kLower === terrainKey || (kLower === 'plain' && terrainKey === 'plains') || (kLower === 'plains' && terrainKey === 'plain')
          })
          const val = foundKey ? tObj[foundKey] : 0
          if (typeof val === 'number') return val
        }
      }
      if (keyStr === 'terrain_main' || keyStr === 'terrain_sub') {
        const type = keyStr === 'terrain_main' ? 'main' : 'sub'
        const tObj = firstData.monster?.terrain?.[`by_${type}`] ?? firstData.monster?.terrain?.[type]
        if (tObj && typeof tObj === 'object') {
          return Object.values(tObj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }
      if (keyStr === 'terrain') {
        const val = firstData.monster?.terrain?.total
        if (typeof val === 'number') return val
        const mObj = firstData.monster?.terrain?.by_main ?? firstData.monster?.terrain?.main
        if (mObj && typeof mObj === 'object') {
          return Object.values(mObj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Crystalize - 95. 結晶化回数 (Number of crystallization cycles)
      if (keyStr === 'crystalization_count_total') {
        const val = firstData.crystalize?.crystalization_count?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'crystalization_count_average') {
        const val = firstData.crystalize?.crystalization_count?.average
        if (typeof val === 'number') return val
      }
      if (keyStr === 'crystalization_count') {
        const val = firstData.crystalize?.crystalization_count?.total
        if (typeof val === 'number') return val
      }

      // Crystalize - 96. 結晶使用回数 (Number of times crystals are used)
      if (keyStr === 'used_crystalize_total') {
        const val = firstData.crystalize?.used_crystalize?.total
        if (typeof val === 'number') return val
      }
      if (keyStr === 'used_crystalize_average') {
        const val = firstData.crystalize?.used_crystalize?.average
        if (typeof val === 'number') return val
      }
      if (keyStr === 'used_crystalize') {
        const val = firstData.crystalize?.used_crystalize?.total
        if (typeof val === 'number') return val
      }

      // Crystalize - 97. 結晶化されたMonster (Crystallized Monster)
      if (keyStr === 'crystalizeed_monster' || keyStr === 'crystalized_monster') {
        const obj = firstData.crystalize?.crystalized_monster ?? firstData.crystalize?.crystalizeed_monster
        if (typeof obj === 'number') return obj
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Crystalize - 98. 結晶化時の平均Overall Ability (Average Overall Ability during Crystallization)
      if (keyStr === 'average_crystalize_overall') {
        const val = firstData.crystalize?.average_crystalize_overall
        if (typeof val === 'number') return val
      }

      // Crystalize - 99. 結晶化時の最大Overall Ability (Maximum Overall Ability during Crystallization)
      if (keyStr === 'max_crystalize_overall') {
        const val = firstData.crystalize?.max_crystalize_overall
        if (typeof val === 'number') return val
      }

      // Crystalize - 100. 結晶化時のMonster Rank (Monster Rank at Crystallization)
      const matchRankCrystalized = keyStr.match(/^monster_rank_crystalized_([a-z])$/i)
      if (matchRankCrystalized) {
        const rank = matchRankCrystalized[1].toLowerCase()
        const val = firstData.crystalize?.monster_rank_crystalized?.[rank]
        if (typeof val === 'number') return val
      }
      if (keyStr === 'monster_rank_crystalized') {
        const obj = firstData.crystalize?.monster_rank_crystalized
        if (typeof obj === 'number') return obj
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Crystalize - 101. 結晶の各種Parameter平均 (Average of various parameters of the crystal: HP~VIT)
      const matchAvgBasicCrystal = keyStr.match(/^average_basic_crystalize_parameter_(hp|str|int|dex|agi|vit)$/i)
      if (matchAvgBasicCrystal) {
        const param = matchAvgBasicCrystal[1].toLowerCase()
        const val = firstData.crystalize?.average_basic_crystalize_parameter?.[param]
        if (typeof val === 'number') return val
      }
      if (keyStr === 'average_basic_crystalize_parameter') {
        const obj = firstData.crystalize?.average_basic_crystalize_parameter
        if (typeof obj === 'number') return obj
        if (obj && typeof obj === 'object') {
          const vals = Object.values(obj).filter((v) => typeof v === 'number')
          if (vals.length > 0) return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100
        }
      }

      // Crystalize - 102. 使用された結晶の各種Parameter平均 (Average of various parameters of the crystal used: HP~VIT)
      const matchAvgBasicCrystalUsed = keyStr.match(/^average_basic_crystalize_parameter_used_(hp|str|int|dex|agi|vit)$/i)
      if (matchAvgBasicCrystalUsed) {
        const param = matchAvgBasicCrystalUsed[1].toLowerCase()
        const val = firstData.crystalize?.average_basic_crystalize_parameter_used?.[param]
        if (typeof val === 'number') return val
      }
      if (keyStr === 'average_basic_crystalize_parameter_used') {
        const obj = firstData.crystalize?.average_basic_crystalize_parameter_used
        if (typeof obj === 'number') return obj
        if (obj && typeof obj === 'object') {
          const vals = Object.values(obj).filter((v) => typeof v === 'number')
          if (vals.length > 0) return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100
        }
      }

      // Crystalize - 103. 結晶のTrait (Crystal Trait)
      if (keyStr === 'total_crystalize_trait') {
        const val = firstData.crystalize?.total_crystalize_trait
        if (typeof val === 'number') return val
      }

      // Crystalize - 104. 使用された結晶のTrait (Trait of the crystal used)
      if (keyStr === 'used_crystalize_trait') {
        const val = firstData.crystalize?.used_crystalize_trait
        if (typeof val === 'number') return val
      }

      // Coach - 105. コーチ化回数 (Number of times coached)
      if (keyStr === 'coach_count_total') {
        const val = firstData.coach?.coach_count?.total ?? (typeof firstData.coach?.coach_count === 'number' ? firstData.coach.coach_count : 0)
        if (typeof val === 'number') return val
      }
      if (keyStr === 'coach_count_average') {
        const val = firstData.coach?.coach_count?.average
        if (typeof val === 'number') return val
      }
      if (keyStr === 'coach_count') {
        const val = firstData.coach?.coach_count?.total ?? (typeof firstData.coach?.coach_count === 'number' ? firstData.coach.coach_count : 0)
        if (typeof val === 'number') return val
      }

      // Coach - 106. コーチ使用回数 (Coach usage count)
      if (keyStr === 'coach_used_total') {
        const val = firstData.coach?.coach_used?.total ?? (typeof firstData.coach?.coach_used === 'number' ? firstData.coach.coach_used : 0)
        if (typeof val === 'number') return val
      }
      if (keyStr === 'coach_used_average') {
        const val = firstData.coach?.coach_used?.average
        if (typeof val === 'number') return val
      }
      if (keyStr === 'coach_used') {
        const val = firstData.coach?.coach_used?.total ?? (typeof firstData.coach?.coach_used === 'number' ? firstData.coach.coach_used : 0)
        if (typeof val === 'number') return val
      }

      // Coach - 107. コーチ化されたMonster (Coached Monster)
      if (keyStr === 'coach_monster') {
        const obj = firstData.coach?.coach_monster
        if (typeof obj === 'number') return obj
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Coach - 108. コーチ化時の平均Overall Ability (Average Overall Ability at the time of becoming a coach)
      if (keyStr === 'average_coach_overall') {
        const val = firstData.coach?.average_coach_overall
        if (typeof val === 'number') return val
      }

      // Coach - 109. コーチ化時の最大Overall Ability (Maximum Overall Ability when becoming a coach)
      if (keyStr === 'max_coach_overall' || keyStr === 'max_overall') {
        const val = firstData.coach?.max_coach_overall ?? firstData.coach?.max_overall
        if (typeof val === 'number') return val
      }

      // Coach - 110. コーチ化時のMonster Rank (Monster Rank when becoming a coach)
      const matchRankCoached = keyStr.match(/^monster_rank_coached_([a-z])$/i)
      if (matchRankCoached) {
        const rank = matchRankCoached[1].toLowerCase()
        const val = firstData.coach?.monster_rank_coached?.[rank]
        if (typeof val === 'number') return val
      }
      if (keyStr === 'monster_rank_coached') {
        const obj = firstData.coach?.monster_rank_coached
        if (typeof obj === 'number') return obj
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Coach - 111. コーチの各種Parameter平均 (Average of various coach parameters: HP~VIT)
      const matchAvgBasicCoach = keyStr.match(/^average_basic_coach_parameter_(hp|str|int|dex|agi|vit)$/i)
      if (matchAvgBasicCoach) {
        const param = matchAvgBasicCoach[1].toLowerCase()
        const val = firstData.coach?.average_basic_coach_parameter?.[param]
        if (typeof val === 'number') return val
      }
      if (keyStr === 'average_basic_coach_parameter') {
        const obj = firstData.coach?.average_basic_coach_parameter
        if (typeof obj === 'number') return obj
        if (obj && typeof obj === 'object') {
          const vals = Object.values(obj).filter((v) => typeof v === 'number')
          if (vals.length > 0) return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100
        }
      }

      // Coach - 112. 使用されたコーチの各種Parameter平均 (Average of various parameters of the coach used: HP~VIT)
      const matchAvgBasicCoachUsed = keyStr.match(/^average_basic_coach_parameter_used_(hp|str|int|dex|agi|vit)$/i)
      if (matchAvgBasicCoachUsed) {
        const param = matchAvgBasicCoachUsed[1].toLowerCase()
        const val = firstData.coach?.average_basic_coach_parameter_used?.[param]
        if (typeof val === 'number') return val
      }
      if (keyStr === 'average_basic_coach_parameter_used') {
        const obj = firstData.coach?.average_basic_coach_parameter_used
        if (typeof obj === 'number') return obj
        if (obj && typeof obj === 'object') {
          const vals = Object.values(obj).filter((v) => typeof v === 'number')
          if (vals.length > 0) return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100
        }
      }

      // Coach - 113. コーチ化で選択されたParameter (Parameters selected for coaching: HP~VIT)
      const matchParamSelectedCoach = keyStr.match(/^parameter_selected_coach_(hp|str|int|dex|agi|vit)$/i)
      if (matchParamSelectedCoach) {
        const param = matchParamSelectedCoach[1].toLowerCase()
        const val = firstData.coach?.parameter_selected_coach?.[param]
        if (typeof val === 'number') return val
      }
      if (keyStr === 'parameter_selected_coach') {
        const obj = firstData.coach?.parameter_selected_coach
        if (typeof obj === 'number') return obj
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Coach - 114. 使用されたコーチのParameter (Parameters of the coach used: HP~VIT)
      const matchParamUsedCoach = keyStr.match(/^parameter_used_coach_(hp|str|int|dex|agi|vit)$/i)
      if (matchParamUsedCoach) {
        const param = matchParamUsedCoach[1].toLowerCase()
        const val = firstData.coach?.parameter_used_coach?.[param]
        if (typeof val === 'number') return val
      }
      if (keyStr === 'parameter_used_coach') {
        const obj = firstData.coach?.parameter_used_coach
        if (typeof obj === 'number') return obj
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Donate - 115. DonateされたMonsterの数 (Number of Monsters Donated)
      if (keyStr === 'donated_monster_count_total') {
        const val = firstData.donate?.donated_monster_count?.total ?? (typeof firstData.donate?.donated_monster_count === 'number' ? firstData.donate.donated_monster_count : 0)
        if (typeof val === 'number') return val
      }
      if (keyStr === 'donated_monster_count_average') {
        const val = firstData.donate?.donated_monster_count?.average
        if (typeof val === 'number') return val
      }
      if (keyStr === 'donated_monster_count') {
        const val = firstData.donate?.donated_monster_count?.total ?? (typeof firstData.donate?.donated_monster_count === 'number' ? firstData.donate.donated_monster_count : 0)
        if (typeof val === 'number') return val
      }

      // Donate - 116. 発行されたMA Pointの数 (Number of MA Points issued)
      if (keyStr === 'ma_point_earned_total') {
        const val = firstData.donate?.ma_point_earned?.total ?? (typeof firstData.donate?.ma_point_earned === 'number' ? firstData.donate.ma_point_earned : 0)
        if (typeof val === 'number') return val
      }
      if (keyStr === 'ma_point_earned_average') {
        const val = firstData.donate?.ma_point_earned?.average
        if (typeof val === 'number') return val
      }
      if (keyStr === 'ma_point_earned') {
        const val = firstData.donate?.ma_point_earned?.total ?? (typeof firstData.donate?.ma_point_earned === 'number' ? firstData.donate.ma_point_earned : 0)
        if (typeof val === 'number') return val
      }

      // Donate - 117. DonateされたMonster (Donated Monster)
      if (keyStr === 'donated_monster') {
        const obj = firstData.donate?.donated_monster
        if (typeof obj === 'number') return obj
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // Donate - 118. Donate時の平均Overall Ability (Average Overall Ability at the time of donation)
      if (keyStr === 'average_donated_overall_ability') {
        const val = firstData.donate?.average_donated_overall_ability
        if (typeof val === 'number') return val
      }

      // Donate - 119. Donate時の最大Overall Ability (Maximum Overall Ability at the time of Donation)
      if (keyStr === 'max_donated_overall_ability') {
        const val = firstData.donate?.max_donated_overall_ability
        if (typeof val === 'number') return val
      }

      // Donate - 120. Donate時のMonster Rank (Monster Rank at the time of Donation)
      const matchRankDonated = keyStr.match(/^monster_rank_donated_([a-z])$/i)
      if (matchRankDonated) {
        const rank = matchRankDonated[1].toLowerCase()
        const val = firstData.donate?.monster_rank_donated?.[rank]
        if (typeof val === 'number') return val
      }
      if (keyStr === 'monster_rank_donated') {
        const obj = firstData.donate?.monster_rank_donated
        if (typeof obj === 'number') return obj
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // ==========================================
      // Farm NFT (Items 121 - 127)
      // ==========================================
      // 121. MintされたFarmの数量 (Number of Farm: total, average)
      if (keyStr === 'number_of_farm_total') {
        const val = firstData.farm?.number_of_farm?.total ?? (typeof firstData.farm?.number_of_farm === 'number' ? firstData.farm.number_of_farm : 0)
        if (typeof val === 'number') return val
      }
      if (keyStr === 'number_of_farm_average') {
        const val = firstData.farm?.number_of_farm?.average
        if (typeof val === 'number') return val
      }
      if (keyStr === 'number_of_farm') {
        const val = firstData.farm?.number_of_farm?.total ?? (typeof firstData.farm?.number_of_farm === 'number' ? firstData.farm.number_of_farm : 0)
        if (typeof val === 'number') return val
      }

      // 122 & 126. Farm Average Parameters (Average Overall & HP~VIT, Stress, Fatigue, Rest)
      if (keyStr === 'farm_average_overall') {
        const val = firstData.farm?.farm_average_parameter?.farm_average_overall
        if (typeof val === 'number') return val
        const b = firstData.farm?.farm_average_parameter?.farm_basic_enhance_value
        if (b && typeof b === 'object') {
          return Math.round(Object.values(b).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0) * 100) / 100
        }
      }
      const matchFarmBasicParam = keyStr.match(/^farm_basic_enhance_value_([a-z]+)$/i)
      if (matchFarmBasicParam) {
        const param = matchFarmBasicParam[1].toLowerCase()
        const val = firstData.farm?.farm_average_parameter?.farm_basic_enhance_value?.[param]
        if (typeof val === 'number') return val
      }
      if (keyStr === 'farm_basic_enhance_value') {
        const obj = firstData.farm?.farm_average_parameter?.farm_basic_enhance_value
        if (obj && typeof obj === 'object') {
          const vals = Object.values(obj).filter((v) => typeof v === 'number')
          if (vals.length > 0) return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100
        }
      }
      if (keyStr === 'farm_stress_enhance_value') {
        const val = firstData.farm?.farm_average_parameter?.farm_stress_enhance_value
        if (typeof val === 'number') return val
      }
      if (keyStr === 'farm_fatigue_enhance_value') {
        const val = firstData.farm?.farm_average_parameter?.farm_fatigue_enhance_value
        if (typeof val === 'number') return val
      }
      if (keyStr === 'farm_rest_enhance_value') {
        const val = firstData.farm?.farm_average_parameter?.farm_rest_enhance_value
        if (typeof val === 'number') return val
      }
      if (keyStr === 'farm_average_parameter') {
        const val = firstData.farm?.farm_average_parameter?.farm_average_overall
        if (typeof val === 'number') return val
      }

      // 123. Farm Suitability (D ~ S rating per parameter)
      const matchFarmSuitabilityRank = keyStr.match(/^farm_suitability_([a-z]+)_([a-z])$/i)
      if (matchFarmSuitabilityRank) {
        const param = matchFarmSuitabilityRank[1].toLowerCase()
        const rank = matchFarmSuitabilityRank[2].toLowerCase()
        const val = firstData.farm?.farm_suitability?.[param]?.[rank]
        if (typeof val === 'number') return val
      }
      const matchFarmSuitabilityParam = keyStr.match(/^farm_suitability_([a-z]+)$/i)
      if (matchFarmSuitabilityParam) {
        const param = matchFarmSuitabilityParam[1].toLowerCase()
        const obj = firstData.farm?.farm_suitability?.[param]
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0)
        }
      }
      if (keyStr === 'farm_suitability') {
        const obj = firstData.farm?.farm_suitability
        if (obj && typeof obj === 'object') {
          let sum = 0
          Object.values(obj).forEach((inner) => {
            if (inner && typeof inner === 'object') {
              sum += Object.values(inner).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0)
            }
          })
          return sum
        }
      }

      // 124. Special Training Parameters (Main, Sub, Decrease & Stats Breakdown)
      const matchSpecialTrainingCategoryStat = keyStr.match(
        /^special_training_value_(main|sub|decrease)_([a-z]+)$/i
      )
      if (matchSpecialTrainingCategoryStat) {
        const cat = matchSpecialTrainingCategoryStat[1].toLowerCase()
        const stat = matchSpecialTrainingCategoryStat[2].toLowerCase()
        const catObj = firstData.farm?.special_training_value?.[cat]
        if (catObj && typeof catObj === 'object') {
          const val = catObj[stat]
          if (typeof val === 'number') return val
        }
        return 0
      }
      if (keyStr === 'special_training_value_main') {
        const catObj = firstData.farm?.special_training_value?.main
        if (catObj && typeof catObj === 'object' && typeof catObj.total === 'number') return catObj.total
        const val = firstData.farm?.special_training_value?.total_main ?? firstData.farm?.special_training_value?.main
        if (typeof val === 'number') return val
        return 0
      }
      if (keyStr === 'special_training_value_sub') {
        const catObj = firstData.farm?.special_training_value?.sub
        if (catObj && typeof catObj === 'object' && typeof catObj.total === 'number') return catObj.total
        const val = firstData.farm?.special_training_value?.total_sub ?? firstData.farm?.special_training_value?.sub
        if (typeof val === 'number') return val
        return 0
      }
      if (keyStr === 'special_training_value_decrease') {
        const catObj = firstData.farm?.special_training_value?.decrease
        if (catObj && typeof catObj === 'object' && typeof catObj.total === 'number') return catObj.total
        const val = firstData.farm?.special_training_value?.total_decrease ?? firstData.farm?.special_training_value?.decrease
        if (typeof val === 'number') return val
        return 0
      }
      const matchSpecialTrainingStat = keyStr.match(/^special_training_value_([a-z]+)$/i)
      if (matchSpecialTrainingStat) {
        const stat = matchSpecialTrainingStat[1].toLowerCase()
        const val = firstData.farm?.special_training_value?.[stat]
        if (typeof val === 'number') return val
        return 0
      }
      if (keyStr === 'special_training_value') {
        const obj = firstData.farm?.special_training_value
        if (obj && typeof obj === 'object') {
          return (obj.total_main || 0) + (obj.total_sub || 0) - (obj.total_decrease || 0)
        }
        return 0
      }

      // 127. Farm Trait (Total & Individual Traits)
      if (keyStr === 'farm_trait_id_total' || keyStr === 'farm_trait_total') {
        const val = firstData.farm?.farm_trait_id?.total
        if (typeof val === 'number') return val
        const obj = firstData.farm?.farm_trait_id
        if (obj && typeof obj === 'object') {
          return Object.entries(obj).reduce((s, [k, v]) => s + (k !== 'total' && typeof v === 'number' ? v : 0), 0)
        }
      }
      const matchFarmTrait = keyStr.match(/^(?:farm_trait_id|farm_trait)_(?:id_)?([a-z0-9_]+)$/i)
      if (matchFarmTrait) {
        const traitSuffix = matchFarmTrait[1].toLowerCase()
        const traitObj = firstData.farm?.farm_trait_id
        if (traitObj && typeof traitObj === 'object') {
          for (const [tKey, tVal] of Object.entries(traitObj)) {
            if (tKey.toLowerCase() === traitSuffix && typeof tVal === 'number') {
              return tVal
            }
          }
        }
      }
      if (keyStr === 'farm_trait' || keyStr === 'farm_trait_id') {
        const val = firstData.farm?.farm_trait_id?.total
        if (typeof val === 'number') return val
        const obj = firstData.farm?.farm_trait_id
        if (obj && typeof obj === 'object') {
          return Object.entries(obj).reduce((s, [k, v]) => s + (k !== 'total' && typeof v === 'number' ? v : 0), 0)
        }
      }

      // 125. Farm Boots Item Used / Enhancement Items Used
      if (
        keyStr === 'farm_boots_item_used' ||
        keyStr === 'farm_boots_item_used_total' ||
        keyStr === 'farm_enhancement_item' ||
        keyStr === 'farm_enhancement_item_total'
      ) {
        const val = firstData.farm?.farm_enhancement_item?.total ?? firstData.farm?.farm_boots_item_used ?? 0
        if (typeof val === 'number') return val
      }

      const matchFarmEnhanceItem = keyStr.match(/^farm_enhancement_item_([a-z_]+)$/i)
      if (matchFarmEnhanceItem) {
        const itemPrefix = matchFarmEnhanceItem[1].toUpperCase()
        const itemsObj = firstData.farm?.farm_enhancement_item?.items
        if (itemsObj && typeof itemsObj === 'object') {
          let sum = 0
          for (const [k, v] of Object.entries(itemsObj)) {
            if (k.toUpperCase().includes(itemPrefix) && typeof v === 'number') {
              sum += v
            }
          }
          return sum
        }
      }

      // ==========================================
      // Item (Items 128 - 131)
      // ==========================================
      // 128. Number of times each item has been issued
      if (keyStr === 'item_mint_count' || keyStr === 'item_mint_count_total') {
        const val = firstData.item?.item_mint_count?.total ?? (typeof firstData.item?.item_mint_count === 'number' ? firstData.item?.item_mint_count : 0)
        if (typeof val === 'number') return val
      }

      // 129. Number of times each item is played / used
      if (keyStr === 'item_used_count' || keyStr === 'item_used_count_total') {
        const val = firstData.item?.item_used_count?.total ?? (typeof firstData.item?.item_used_count === 'number' ? firstData.item?.item_used_count : 0)
        if (typeof val === 'number') return val
      }

      // 130. Number of each item in circulation
      if (keyStr === 'item_distribution' || keyStr === 'item_distribution_total') {
        const val = firstData.item?.item_distribution?.total ?? (typeof firstData.item?.item_distribution === 'number' ? firstData.item?.item_distribution : 0)
        if (typeof val === 'number') return val
        const obj = firstData.item?.item_distribution
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }

      // 131. Issuance (acquisition) route for each item
      if (keyStr === 'item_acquisition' || keyStr === 'item_acquisition_total') {
        const val = firstData.item?.item_acquisition?.total ?? (typeof firstData.item?.item_acquisition === 'number' ? firstData.item?.item_acquisition : 0)
        if (typeof val === 'number') return val
        const obj = firstData.item?.item_acquisition
        if (obj && typeof obj === 'object') {
          return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }
      if (keyStr === 'item_acquisition_tournament') {
        const val = firstData.item?.item_acquisition?.tournament?.total ?? firstData.item?.item_acquisition?.tournament
        if (typeof val === 'number') return val
      }
      if (keyStr === 'item_acquisition_exploration') {
        const val = firstData.item?.item_acquisition?.exploration?.total ?? firstData.item?.item_acquisition?.exploration
        if (typeof val === 'number') return val
      }
      if (keyStr === 'item_acquisition_prize_rank_match' || keyStr === 'item_acquisition_prized_battle') {
        const val = firstData.item?.item_acquisition?.prize_rank_match?.total ?? firstData.item?.item_acquisition?.prized_battle?.total ?? firstData.item?.item_acquisition?.prized_battle
        if (typeof val === 'number') return val
      }
      if (keyStr === 'item_acquisition_shop') {
        const val = firstData.item?.item_acquisition?.shop
        if (typeof val === 'number') return val
      }
      if (keyStr === 'item_acquisition_login_bonus') {
        const val = firstData.item?.item_acquisition?.login_bonus
        if (typeof val === 'number') return val
      }
      if (keyStr === 'item_acquisition_other') {
        const val = firstData.item?.item_acquisition?.other
        if (typeof val === 'number') return val
      }
    }
 
    if (openKeys && openKeys.length > 0) {
      const transformedKeys = transformKeys(openKeys)
      let value = getNestedValue(firstData, transformedKeys)

      if (value && typeof value === 'number') return value
      if (value && value[dataItem?.value] !== undefined && value[dataItem?.value] !== null) {
        const val = value[dataItem?.value]
        if (typeof val === 'number') return val
        if (Array.isArray(val)) return val.length
        if (typeof val === 'object') {
          if (typeof val.total === 'number') return val.total
          return Object.values(val).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
        }
      }
    }

    return 0
  }, [])

  const data = useMemo(() => ({
    labels,
    datasets: checkedList?.map((dataItem) => {
      const color = getRandomColor()
      let label = dataItem?.label
      if (!label) {
        try {
          const translated = t(`item.${dataItem?.key}`)
          label = translated && translated !== `item.${dataItem?.key}` ? translated : dataItem?.key
        } catch (e) {
          label = dataItem?.key
        }
      }
      const obj = {
        label,
        data: dataChart?.map((game, index) => {
          return resolveMetricValue(game?.firstData, stateOpenKeys, dataItem)
        }),
        borderColor: color,
        backgroundColor: color,
      }

      return obj
    }),
  }), [checkedList, dataChart, stateOpenKeys, labels, resolveMetricValue, t])

  useEffect(() => {
    const currentDate = new Date()
    currentDate.setHours(0, 0, 0, 0)
    const startDate = new Date(currentDate.getTime() - 30 * 24 * 60 * 60 * 1000)
    const endDate = currentDate
    const params = {
      startDate: startDate.toISOString().slice(0, 10),
      endDate: endDate.toISOString().slice(0, 10),
    }

    if (date[0] !== '') {
      let temp = date[0].split('-')
      let temp1 = date[1].split('-')
      params.startDate = `${temp[0]}-${temp[1]}-${temp[2]}`
      params.endDate = `${temp1[0]}-${temp1[1]}-${temp1[2]}`
    }
    if (handleChangeParam) {
      handleChangeParam(params)
    }
    actions.getDashBoardGame(params, (res) => {
      setDataChart(res.data)
    })
  }, [date])

  const disabledDate = (current) => {
    return current && current > moment().endOf('day')
  }
  const onChangeDate = (values) => {
    setDate(values)
  }
  const handleSwitchChange = (checked, item) => {
    setCheckedList((prevList) => {
      if (checked) {
        const newItem = {
          key: item.key,
          value: item.value,
          rawTz: item.rawTz,
          label: item.chartLabel || item.label,
        }
        if (prevList.length >= 5) {
          return [...prevList.slice(1), newItem]
        }
        return [...prevList, newItem]
      } else {
        return prevList.filter((i) => i.key !== item.key)
      }
    })

    setActiveMenuChild((prev) =>
      checked ? item : prev.key === item.key ? {} : prev,
    )
  }

  const handleMenuClickChild = (item) => {
    setActiveMenuChild(item)
  }

  const renderSwitches = (children) => {
    return children.map((item) => {
      if (item.children) {
        return (
          <Menu.SubMenu
            key={item.key}
            title={item.label}
            onTitleClick={() => handleMenuClickChild(item)}
          >
            {renderSwitches(item.children)}
          </Menu.SubMenu>
        )
      } else {
        const isChecked = checkedList?.some((i) => i.key === item.key)

        return (
          <Menu.Item key={item.key} style={{ marginBottom: 10 }}>
            <Row>
              <Col span={18}>
                <span>{item.label}</span>
              </Col>
              <Col span={4}>
                <Switch
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                  checked={isChecked}
                  onChange={(checked) => {
                    handleSwitchChange(checked, item)
                    if (checked) {
                      setActiveMenuChild(item)
                    }
                  }}
                />
              </Col>
            </Row>
          </Menu.Item>
        )
      }
    })
  }

  const onOpenChange = (openKeys) => {
    const currentOpenKey = openKeys.find(
      (key) => stateOpenKeys.indexOf(key) === -1,
    )
    if (currentOpenKey !== undefined) {
      const repeatIndex = openKeys
        .filter((key) => key !== currentOpenKey)
        .findIndex((key) => dynamicLevelKeys[key] === dynamicLevelKeys[currentOpenKey])
      setStateOpenKeys(
        openKeys
          .filter((_, index) => index !== repeatIndex)
          .filter((key) => dynamicLevelKeys[key] <= dynamicLevelKeys[currentOpenKey]),
      )
    } else {
      setStateOpenKeys(openKeys)
    }
  }
  return (
    <>
      <div>
        <Row gutter={24} className="equal-height-row-GameDashboard">
          <Col span={18} className="equal-height-col-GameDashboard">
            <Card
              title=""
              extra={
                <div className="flex gap-4 items-center">
                  <Popover
                    className="w-full"
                    trigger="click"
                    placement="bottom"
                    arrow={false}
                  >
                    <Button className="!bg-[#00113D] border__grey hover:!border-[#40A9FF] h-[32.19px] !w-[210px]"></Button>
                  </Popover>
                  <RangePicker
                    className="h-full max-w-[256px]"
                    onChange={(values, dateString) => onChangeDate(dateString)}
                    disabledDate={disabledDate}
                  />
                </div>
              }
            >
              <div>{t(`gameDashboard.unit`)}</div>
              <Line options={options} data={data} />
            </Card>
          </Col>
          <Col span={6} className="equal-height-col-GameDashboard">
            <Card className="gameFilter" title="">
              <Row>
                <Col span={24}>
                  <div
                    className="gameMenu"
                    style={{
                      overflowY: 'auto',
                      minHeight: '700px',
                      maxHeight: '746px',
                    }}
                  >
                    <Menu
                      mode="inline"
                      onOpenChange={onOpenChange}
                      openKeys={stateOpenKeys}
                    >
                      {dynamicItemsGameDashboard.map((item) => (
                        <Menu.SubMenu key={item.key} title={item.label}>
                          {renderSwitches(item.children)}
                        </Menu.SubMenu>
                      ))}
                    </Menu>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default GameChart
